import { array, shape, string } from 'prop-types';
import React, { Fragment } from 'react';
import { withRouter } from 'next/router';
import AmpHeader from 'components/AmpHeader';
import AmpFooter from 'components/AmpFooter';
import AmpOutbrain from 'components/AmpOutbrain';
import AmpSpread from 'components/AmpSpread';
import AmpAnalytics from 'components/AmpAnalytics';
import AmpStickyAd from 'components/AmpStickyAd';
import PostBundle from 'components/PostBundle';
import AmpSkimlinks from 'components/AmpSkimlinks';
import Metadata from 'components/Metadata';
import CoverMedia from 'components/CoverMedia';
import AmpGallery from 'components/AmpGallery';
import AmpImg from 'components/AmpImg';
import AmpVideo from 'components/AmpVideo';
import { getBundle } from 'util/bundles';
import { getCleanPath } from 'util/link';
import { getSiteHeaderSettings } from 'util/header';
import { HAS_RECIRC } from 'constants/site';
import { AMP_OUTBRAIN_WIDGET_IDS } from 'constants/vendor';
import { POST } from 'constants/pageTypes';
import { AMP_RUBICON_ID } from 'constants/ads/prebid';
import styles from './styles';

const COVER_MEDIA_COMPONENTS = {
  gallery: AmpGallery,
  image: AmpImg,
  Video: AmpVideo
};

const triggers = {
  teaser: {
    on: 'click',
    selector: '.AmpSpread__item--teaser a',
    request: 'event',
    vars: {
      eventCategory: 'Teaser',
      eventAction: 'Click',
      eventLabel: 'AMP'
    }
  }
};

function AmpPostLayout({ pageBundles, contentBundles, router }) {
  const metadata = getBundle(pageBundles, 'metadata');
  const post = contentBundles.find(b => b.layout === 'post');
  const recirc = HAS_RECIRC ? contentBundles.find(b => b.layout === 'r') : null;

  const currentPath = router.asPath
    ? getCleanPath(router.asPath)
    : router.pathname;

  const { logo } = getSiteHeaderSettings(currentPath);

  return (
    <Fragment>
      {metadata && metadata.items && (
        <Fragment>
          <Metadata items={metadata.items} />
          <AmpAnalytics
            metadataItems={metadata.items}
            pageType={POST}
            triggers={triggers}
          />
        </Fragment>
      )}
      <AmpHeader logo={logo} />
      {/* For MVP we only want to render the post bundle */}
      <main className="AmpPostLayout">
        {post && (
          <Fragment>
            {!post.hideAds && AMP_RUBICON_ID && (
              <amp-iframe
                width="1"
                title="User Sync"
                height="1"
                sandbox="allow-scripts"
                frameborder="0"
                src={`https://ads.rubiconproject.com/prebid/load-cookie.html?endpoint=rubicon&max_sync_count=5&args=account:${AMP_RUBICON_ID}`}
              >
                <amp-img
                  layout="fill"
                  src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                  placeholder
                />
              </amp-iframe>
            )}
            <PostBundle
              {...post}
              coverMedia={
                post.data.coverMedia && (
                  <div className="AmpPostLayout__cover">
                    <CoverMedia
                      components={COVER_MEDIA_COMPONENTS}
                      type={post.data.coverMedia.type}
                      data={post.data.coverMedia.data}
                      post={{
                        ads: post.data.ads,
                        canonical: post.data.canonical,
                        title: post.data.title
                      }}
                    />
                  </div>
                )
              }
              // Comments are not supported in AMP
              comments={null}
              isAmp
            />
            {recirc && (
              <div className="AmpPostLayout__recirc">
                <AmpSpread {...recirc} />
              </div>
            )}
            <div className="AmpPostLayout__outbrain">
              <AmpOutbrain
                canonical={post.data.canonical}
                widgetIds={AMP_OUTBRAIN_WIDGET_IDS}
              />
            </div>
            {post.isAffiliate && <AmpSkimlinks />}
            {!(post.hideAds || post.isSponsored) && <AmpStickyAd post={post} />}
          </Fragment>
        )}
      </main>
      <AmpFooter />
      <style jsx global>
        {styles}
      </style>
    </Fragment>
  );
}

AmpPostLayout.defaultProps = {
  pageBundles: undefined,
  contentBundles: undefined
};

AmpPostLayout.propTypes = {
  pageBundles: array, // eslint-disable-line react/forbid-prop-types
  contentBundles: array, // eslint-disable-line react/forbid-prop-types
  router: shape({
    pathname: string.isRequired,
    query: shape({}).isRequired,
    asPath: string.isRequired
  }).isRequired
};

export default withRouter(AmpPostLayout);
